import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getProjectEntries } from '../services/ProjectServices';
import {
    getTicketsByProject,
    getRecentTickets,
    createTicket,
    updateTicket,
    deleteTicket,
} from '../services/ProjectTicketService';
import { getAllocationsByTicket } from '../services/ProjectAllocationService';
import { getProjectHoursSummary } from '../services/OrderService';
import { extractApiError } from '../utils/apiError';
import { useToast, useConfirm } from '../context/UIContext';
import '../styles/ProjectTickets.css';

const EMPTY_FORM = { title: '', description: '', external_link: '', is_done: false };

function fmtMinutes(min) {
    if (!min && min !== 0) return '—';
    const h = Math.floor(Math.abs(min) / 60);
    const m = Math.abs(min) % 60;
    return `${h}h ${m.toString().padStart(2, '0')}min`;
}

export default function ProjectTickets() {
    const [projects, setProjects] = useState([]);
    const [search, setSearch] = useState('');
    const [selectedProject, setSelectedProject] = useState(null);
    const [recent, setRecent] = useState([]);
    const [loadingRecent, setLoadingRecent] = useState(true);

    const [tickets, setTickets] = useState([]);
    const [ticketForm, setTicketForm] = useState(EMPTY_FORM);
    const [showAddForm, setShowAddForm] = useState(false);
    const [formError, setFormError] = useState('');

    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({});

    // ticket_id -> { total_minutes, allocations, loading }
    const [statsMap, setStatsMap] = useState({});
    const [expandedId, setExpandedId] = useState(null);
    const [doneSearch, setDoneSearch] = useState('');
    // ticket_id -> { ordered_hours, free_hours }
    const [orderMap, setOrderMap] = useState({});

    const toast = useToast();
    const confirm = useConfirm();
    const navigate = useNavigate();
    // `?project=<id>` from the Projekt page. Applied once, so picking another project
    // afterwards is not snapped back.
    const [searchParams, setSearchParams] = useSearchParams();
    const deepLinkApplied = useRef(false);

    useEffect(() => {
        getProjectEntries()
            .then(setProjects)
            .catch(() => {});
        getRecentTickets(30)
            .then(setRecent)
            .catch(() => setRecent([]))
            .finally(() => setLoadingRecent(false));
    }, []);

    const loadTickets = useCallback(async (projectId) => {
        try {
            setTickets(await getTicketsByProject(projectId));
        } catch {
            setTickets([]);
        }
    }, []);

    useEffect(() => {
        if (!selectedProject) { setOrderMap({}); return; }
        getProjectHoursSummary(selectedProject.id)
            .then(summary => {
                const map = {};
                (summary.tickets || []).forEach(t => {
                    map[t.ticket_id] = {
                        ordered_hours: t.ordered_hours,
                        free_hours: t.free_hours,
                    };
                });
                setOrderMap(map);
            })
            .catch(() => setOrderMap({}));
    }, [selectedProject]);

    useEffect(() => {
        if (tickets.length === 0) return;
        const initial = {};
        tickets.forEach(t => { initial[t.id] = { loading: true, total_minutes: 0, allocations: [] }; });
        setStatsMap(initial);
        tickets.forEach(ticket => {
            getAllocationsByTicket(ticket.id)
                .then(data => setStatsMap(prev => ({ ...prev, [ticket.id]: { loading: false, ...data } })))
                .catch(() => setStatsMap(prev => ({ ...prev, [ticket.id]: { loading: false, total_minutes: 0, allocations: [] } })));
        });
    }, [tickets]);

    const handleSelectProject = (project) => {
        setSelectedProject(project);
        // `replace`, so clicking through projects does not fill the history.
        setSearchParams(project?.id ? { project: project.id } : {}, { replace: true });
        setTickets([]);
        setTicketForm(EMPTY_FORM);
        setShowAddForm(false);
        setFormError('');
        setEditingId(null);
        setExpandedId(null);
        setDoneSearch('');
        loadTickets(project.id);
    };

    // `getProjectEntries()` omits archived projects, hence the wider retry — there is no
    // GET /Project/{id} to ask for just the one.
    useEffect(() => {
        const wanted = searchParams.get('project');
        if (!wanted || deepLinkApplied.current || projects.length === 0) return;
        deepLinkApplied.current = true;

        const found = projects.find(p => p.id === wanted);
        if (found) { handleSelectProject(found); return; }

        let cancelled = false;
        getProjectEntries(true)
            .then(all => {
                if (cancelled) return;
                const archived = all.find(p => p.id === wanted);
                if (archived) handleSelectProject(archived);
                else toast('Projekt nicht gefunden.');
            })
            .catch(() => { if (!cancelled) toast('Projekt konnte nicht geladen werden.'); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects, searchParams]);

    const handleOpenRecent = (r) => {
        const proj = projects.find(p => p.id === r.project_id)
            || { id: r.project_id, name: r.project_name, companyName: '' };
        handleSelectProject(proj);
    };

    const handleAddTicket = async (e) => {
        e.preventDefault();
        setFormError('');
        if (!ticketForm.title.trim()) { setFormError('Titel ist erforderlich.'); return; }
        try {
            await createTicket({
                project_id: selectedProject.id,
                title: ticketForm.title.trim(),
                description: ticketForm.description || null,
                external_link: ticketForm.external_link || null,
                is_done: false,
            });
            setTicketForm(EMPTY_FORM);
            setShowAddForm(false);
            await loadTickets(selectedProject.id);
        } catch {
            setFormError('Ticket konnte nicht erstellt werden.');
        }
    };

    const handleStartEdit = (ticket) => {
        setEditingId(ticket.id);
        setEditForm({
            title: ticket.title || '',
            description: ticket.description || '',
            external_link: ticket.external_link || '',
            is_done: ticket.is_done,
        });
    };

    const handleSaveEdit = async (ticketId) => {
        if (!editForm.title.trim()) { toast('Titel ist erforderlich.'); return; }
        try {
            await updateTicket(ticketId, {
                title: editForm.title.trim(),
                description: editForm.description || null,
                external_link: editForm.external_link || null,
                is_done: editForm.is_done,
            });
            setEditingId(null);
            await loadTickets(selectedProject.id);
        } catch (err) {
            toast(extractApiError(err, 'Speichern fehlgeschlagen.'));
        }
    };

    const handleToggleDone = async (ticket) => {
        try {
            await updateTicket(ticket.id, {
                title: ticket.title,
                description: ticket.description,
                external_link: ticket.external_link,
                is_done: !ticket.is_done,
            });
            await loadTickets(selectedProject.id);
        } catch (err) {
            toast(extractApiError(err, 'Status konnte nicht geändert werden.'));
        }
    };

    const handleDelete = async (ticketId) => {
        if (!await confirm('Ticket wirklich löschen?', { confirmLabel: 'Löschen', danger: true })) return;
        try {
            await deleteTicket(ticketId);
            if (expandedId === ticketId) setExpandedId(null);
            await loadTickets(selectedProject.id);
        } catch (err) {
            toast(extractApiError(err, 'Löschen fehlgeschlagen.'));
        }
    };

    const handleToggleExpand = async (ticketId) => {
        if (expandedId === ticketId) {
            setExpandedId(null);
            return;
        }
        setExpandedId(ticketId);
        if (!statsMap[ticketId]) {
            setStatsMap(prev => ({ ...prev, [ticketId]: { loading: true, total_minutes: 0, allocations: [] } }));
            try {
                const data = await getAllocationsByTicket(ticketId);
                setStatsMap(prev => ({ ...prev, [ticketId]: { loading: false, ...data } }));
            } catch {
                setStatsMap(prev => ({ ...prev, [ticketId]: { loading: false, total_minutes: 0, allocations: [] } }));
            }
        }
    };

    const filtered = projects.filter(p =>
        (p.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.companyName || '').toLowerCase().includes(search.toLowerCase())
    );

    const openTickets = tickets.filter(t => !t.is_done);
    const doneTickets = tickets.filter(t => t.is_done);
    const doneQuery = doneSearch.trim().toLowerCase();
    const doneFiltered = doneQuery
        ? doneTickets.filter(t =>
            (t.title || '').toLowerCase().includes(doneQuery) ||
            (t.description || '').toLowerCase().includes(doneQuery))
        : doneTickets;

    return (
        <div className="pt-page">
            <div className="pt-sidebar">
                <h3 className="pt-sidebar-title">Projekte</h3>
                <input
                    className="pt-search"
                    type="text"
                    placeholder="Projekt suchen..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <div className="pt-project-list">
                    {filtered.map(p => (
                        <div
                            key={p.id}
                            className={`pt-project-item ${selectedProject?.id === p.id ? 'pt-project-item--active' : ''}`}
                            onClick={() => handleSelectProject(p)}
                        >
                            <span className="pt-project-company">{p.companyName}</span>
                            <span className="pt-project-name">{p.name}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="pt-main">
                {!selectedProject ? (
                    <div className="pt-recent">
                        <div className="pt-header">
                            <div>
                                <h2 className="pt-project-title">Zuletzt bearbeitete Tickets</h2>
                                <span className="pt-project-subtitle">
                                    Über alle Projekte · links ein Projekt wählen zum Bearbeiten
                                </span>
                            </div>
                            <button className="button" onClick={() => navigate('/move-allocations')}>
                                Tickets verschieben
                            </button>
                        </div>
                        {loadingRecent ? (
                            <p className="pt-no-tickets">Lade…</p>
                        ) : recent.length === 0 ? (
                            <p className="pt-no-tickets">Noch keine gebuchten Tickets.</p>
                        ) : (
                            <div className="pt-recent-grid">
                                {recent.map(r => (
                                    <button
                                        key={r.id}
                                        className="pt-recent-card"
                                        onClick={() => handleOpenRecent(r)}
                                        title={`${r.project_name} — ${r.title}`}
                                    >
                                        <div className="pt-recent-card-top">
                                            <span className="pt-recent-card-project">{r.project_name}</span>
                                            <span className="pt-recent-card-hours">{fmtMinutes(r.total_minutes)}</span>
                                        </div>
                                        <div className="pt-recent-card-title">
                                            <span className={`pt-status-dot ${r.is_done ? 'pt-status-dot--done' : ''}`} />
                                            {r.title}
                                        </div>
                                        <div className="pt-recent-card-footer">
                                            Zuletzt: {r.last_activity} · {r.booking_count} {r.booking_count === 1 ? 'Buchung' : 'Buchungen'}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="pt-header">
                            <div>
                                <button className="pt-back-link" onClick={() => setSelectedProject(null)}>
                                    ‹ Übersicht
                                </button>
                                <h2 className="pt-project-title">{selectedProject.name}</h2>
                                <span className="pt-project-subtitle">{selectedProject.companyName}</span>
                            </div>
                            <div className="pt-header-actions">
                                <button className="button button-secondary" onClick={() => navigate('/move-allocations')}>
                                    Tickets verschieben
                                </button>
                                <button className="button" onClick={() => { setShowAddForm(v => !v); setFormError(''); }}>
                                    {showAddForm ? 'Abbrechen' : '+ Neues Ticket'}
                                </button>
                            </div>
                        </div>

                        {showAddForm && (
                            <form className="pt-add-form" onSubmit={handleAddTicket}>
                                <h4 className="pt-add-form-title">Neues Ticket</h4>
                                <div className="pt-form-row">
                                    <input
                                        className="pt-input"
                                        type="text"
                                        placeholder="Titel *"
                                        value={ticketForm.title}
                                        onChange={e => setTicketForm(p => ({ ...p, title: e.target.value }))}
                                    />
                                    <input
                                        className="pt-input"
                                        type="text"
                                        placeholder="Beschreibung"
                                        value={ticketForm.description}
                                        onChange={e => setTicketForm(p => ({ ...p, description: e.target.value }))}
                                    />
                                    <input
                                        className="pt-input"
                                        type="text"
                                        placeholder="Externer Link"
                                        value={ticketForm.external_link}
                                        onChange={e => setTicketForm(p => ({ ...p, external_link: e.target.value }))}
                                    />
                                </div>
                                {formError && <p className="pt-error">{formError}</p>}
                                <button className="button" type="submit">Ticket erstellen</button>
                            </form>
                        )}

                        {tickets.length === 0 ? (
                            <p className="pt-no-tickets">Noch keine Tickets für dieses Projekt.</p>
                        ) : (
                            <>
                                {openTickets.length > 0 && (
                                    <section className="pt-section">
                                        <h4 className="pt-section-heading">
                                            Offen <span className="pt-badge pt-badge--open">{openTickets.length}</span>
                                        </h4>
                                        {openTickets.map(ticket => (
                                            <TicketCard
                                                key={ticket.id}
                                                ticket={ticket}
                                                isEditing={editingId === ticket.id}
                                                editForm={editForm}
                                                setEditForm={setEditForm}
                                                isExpanded={expandedId === ticket.id}
                                                stats={statsMap[ticket.id]}
                                                orderInfo={orderMap[ticket.id]}
                                                onToggleDone={handleToggleDone}
                                                onStartEdit={handleStartEdit}
                                                onSaveEdit={handleSaveEdit}
                                                onCancelEdit={() => setEditingId(null)}
                                                onDelete={handleDelete}
                                                onToggleExpand={handleToggleExpand}
                                            />
                                        ))}
                                    </section>
                                )}
                                {doneTickets.length > 0 && (
                                    <section className="pt-section">
                                        <div className="pt-section-head">
                                            <h4 className="pt-section-heading">
                                                Erledigt <span className="pt-badge pt-badge--done">{doneTickets.length}</span>
                                            </h4>
                                            <input
                                                className="pt-search pt-done-search"
                                                type="text"
                                                placeholder="Erledigte Tickets suchen…"
                                                value={doneSearch}
                                                onChange={e => setDoneSearch(e.target.value)}
                                            />
                                        </div>
                                        {doneFiltered.length === 0 ? (
                                            <p className="pt-no-tickets">Keine erledigten Tickets gefunden.</p>
                                        ) : doneFiltered.map(ticket => (
                                            <TicketCard
                                                key={ticket.id}
                                                ticket={ticket}
                                                isEditing={editingId === ticket.id}
                                                editForm={editForm}
                                                setEditForm={setEditForm}
                                                isExpanded={expandedId === ticket.id}
                                                stats={statsMap[ticket.id]}
                                                orderInfo={orderMap[ticket.id]}
                                                onToggleDone={handleToggleDone}
                                                onStartEdit={handleStartEdit}
                                                onSaveEdit={handleSaveEdit}
                                                onCancelEdit={() => setEditingId(null)}
                                                onDelete={handleDelete}
                                                onToggleExpand={handleToggleExpand}
                                            />
                                        ))}
                                    </section>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

function TicketCard({
    ticket,
    isEditing, editForm, setEditForm,
    isExpanded, stats, orderInfo,
    onToggleDone, onStartEdit, onSaveEdit, onCancelEdit, onDelete, onToggleExpand,
}) {
    return (
        <div className={`pt-ticket ${ticket.is_done ? 'pt-ticket--done' : ''}`}>
            <div className="pt-ticket-header">
                <button
                    className={`pt-status-btn ${ticket.is_done ? 'pt-status-btn--done' : ''}`}
                    onClick={() => onToggleDone(ticket)}
                    title={ticket.is_done ? 'Als offen markieren' : 'Als erledigt markieren'}
                >
                    {ticket.is_done ? '✓' : '○'}
                </button>

                {isEditing ? (
                    <div className="pt-edit-form">
                        <input
                            className="pt-input"
                            type="text"
                            placeholder="Titel *"
                            value={editForm.title}
                            onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))}
                        />
                        <input
                            className="pt-input"
                            type="text"
                            placeholder="Beschreibung"
                            value={editForm.description}
                            onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
                        />
                        <input
                            className="pt-input"
                            type="text"
                            placeholder="Externer Link"
                            value={editForm.external_link}
                            onChange={e => setEditForm(p => ({ ...p, external_link: e.target.value }))}
                        />
                        <div className="pt-edit-actions">
                            <button className="button btn-sm" onClick={() => onSaveEdit(ticket.id)}>Speichern</button>
                            <button className="button btn-sm" onClick={onCancelEdit}>Abbrechen</button>
                        </div>
                    </div>
                ) : (
                    <div className="pt-ticket-body">
                        <div className="pt-ticket-title">{ticket.title}</div>
                        {ticket.description && (
                            <div className="pt-ticket-desc">{ticket.description}</div>
                        )}
                        {ticket.external_link && (
                            <a
                                className="pt-ticket-link"
                                href={ticket.external_link}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {ticket.external_link}
                            </a>
                        )}
                    </div>
                )}

                {!isEditing && stats && !stats.loading && stats.total_minutes > 0 && (
                    <span className="pt-booked-chip" title="Insgesamt gebuchte Zeit">
                        {fmtMinutes(stats.total_minutes)} gebucht
                    </span>
                )}

                {!isEditing && (
                    <div className="pt-ticket-actions">
                        <button
                            className={`pt-expand-btn ${isExpanded ? 'pt-expand-btn--open' : ''}`}
                            onClick={() => onToggleExpand(ticket.id)}
                            title="Buchungen anzeigen"
                        >
                            ▸ Stats
                        </button>
                        <button className="button btn-sm" onClick={() => onStartEdit(ticket)}>Bearbeiten</button>
                        <button className="button button-danger btn-sm" onClick={() => onDelete(ticket.id)}>&times;</button>
                    </div>
                )}
            </div>

            {isExpanded && (
                <div className="pt-stats">
                    {!stats || stats.loading ? (
                        <p className="pt-stats-loading">Lade Buchungen...</p>
                    ) : (
                        <>
                            <div className="pt-stats-summary">
                                {orderInfo && (
                                    <>
                                        <span className="pt-stats-label">Auftragsbudget:</span>
                                        <span className="pt-stats-value pt-stats-value--highlight">
                                            {fmtMinutes(Math.round(orderInfo.ordered_hours * 60))}
                                        </span>
                                        <span className="pt-stats-divider" />
                                    </>
                                )}
                                <span className="pt-stats-label">Gebucht:</span>
                                <span className={`pt-stats-value pt-stats-value--highlight${orderInfo && orderInfo.free_hours < 0 ? ' pt-stats-value--over' : ''}`}>
                                    {fmtMinutes(stats.total_minutes)}
                                </span>
                                {orderInfo && (
                                    <>
                                        <span className="pt-stats-divider" />
                                        <span className="pt-stats-label">Frei:</span>
                                        <span className={`pt-stats-value pt-stats-value--highlight${orderInfo.free_hours < 0 ? ' pt-stats-value--over' : ''}`}>
                                            {fmtMinutes(Math.round(orderInfo.free_hours * 60))}
                                        </span>
                                    </>
                                )}
                            </div>
                            {stats.allocations.length === 0 ? (
                                <p className="pt-stats-empty">Keine Buchungen vorhanden.</p>
                            ) : (
                                <table className="pt-alloc-table table--cards">
                                    <thead>
                                        <tr>
                                            <th>Datum</th>
                                            <th>Mitarbeiter</th>
                                            <th>Zeit</th>
                                            <th>Aktivität</th>
                                            <th>Kommentar</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {stats.allocations.map(a => (
                                            <tr key={a.id}>
                                                <td data-label="Datum">{a.date}</td>
                                                <td data-label="Mitarbeiter">{a.user_name || '—'}</td>
                                                <td data-label="Zeit">{fmtMinutes(a.allocated_minutes)}</td>
                                                <td data-label="Aktivität">{a.activity || '—'}</td>
                                                <td className="table-card-actions">{a.comment || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
